/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, July 11th 2022, 7:07:27 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { STATUS_CODES } from "lib/api/types";
import { PrimaryKeyValue } from "lib/types";
import { ActionSnackbar, useToggleState, OSActionButton, OSLoadingButton } from "ui-widgets";
import { ActionDialog, ActionDialogProps } from "ui-widgets/dialogs/GenericDialogs";
import { useDeleteOSItem, useRestoreOSRevision } from "version-control/services";

export interface DeleteOSItemDialogProps<T> extends Pick<ActionDialogProps, "children"> {
  itemId: PrimaryKeyValue<T>;
  useDeleteOSItem: () => ReturnType<typeof useDeleteOSItem>;
  onDelete: () => void;
  onRestore: (item: T) => void;
  openDialog?: boolean;
  handleCloseDialog?: () => void;
  dialogTitle?: string;
  dialogMessage?: React.ReactNode;
}

export const DeleteOSItemDialog = <T,>({
  useDeleteOSItem,
  itemId,
  onDelete,
  openDialog,
  handleCloseDialog,
  onRestore,
  children,
  dialogTitle,
  dialogMessage,
}: DeleteOSItemDialogProps<T>) => {
  const {
    deleteItem,
    versionId,
    isSuccessfulAPIRequest: isSuccessfulItemDeletion,
    isMakingAPIRequest: isDeletingItem,
    isAPIError,
    httpResponseStatusCode,
  } = useDeleteOSItem?.() ?? {};

  React.useEffect(() => {
    if (!isSuccessfulItemDeletion) return;

    handleCloseDialog?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessfulItemDeletion]);

  const { open: openError, handleClose: handleCloseError, handleOpen: handleOpenError } = useToggleState();

  const {
    open: openDeleteSuccess,
    handleClose: handleCloseDeleteSuccess,
    handleOpen: handleOpenDeleteSuccess,
  } = useToggleState();

  React.useEffect(() => {
    if (isAPIError) handleOpenError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAPIError]);

  React.useEffect(() => {
    if (isSuccessfulItemDeletion) handleOpenDeleteSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessfulItemDeletion]);

  const { restoreVersion } = useRestoreOSRevision();

  return (
    <>
      <ActionDialog
        severity="error"
        title={dialogTitle ?? "Delete this item?"}
        open={openDialog}
        onClose={handleCloseDialog}
        children={children}
        renderActions={({ handleClose }) => (
          <>
            <OSActionButton onClick={handleClose} variant="text" size="large">
              Cancel
            </OSActionButton>
            <OSLoadingButton
              variant="contained"
              size="large"
              isLoading={isDeletingItem}
              onClick={() => {
                itemId &&
                  deleteItem(itemId, {
                    onDelete: (itemId) => {
                      onDelete();
                      handleClose?.();
                    },
                  });
              }}
            >
              Delete
            </OSLoadingButton>
          </>
        )}
        dialogMessage={dialogMessage ?? <>It will be kept in your Recycle Bin for 60 days.</>}
      />
      <ActionSnackbar
        open={openDeleteSuccess}
        actionText={<>Undo</>}
        actionFunction={() => {
          versionId && restoreVersion(versionId, onRestore);
          handleCloseDeleteSuccess();
        }}
        autoHideDuration={60000}
        message={<>Successfully deleted item.</>}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        handleClose={handleCloseDeleteSuccess}
        showCloseButton={false}
      />
      <ActionDialog
        severity="error"
        title="Cannot delete item"
        open={openError}
        onClose={handleCloseError}
        renderActions={({ handleClose }) => (
          <>
            <OSActionButton onClick={handleClose} variant="text" size="large">
              Ok
            </OSActionButton>
          </>
        )}
        dialogMessage={
          httpResponseStatusCode >= STATUS_CODES.ServerError
            ? "An unexpected error occured when trying to delete this item. Kindly contact us if this issue persists."
            : "This item cannot be deleted because their is critical data linked to it. You can archive it instead."
        }
      />
    </>
  );
};
